import { Injectable } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { TimestampsProps } from '@oogaday/shared';
import { Model } from '../model/common.model';
import { IBettingOption } from '../modules/form-actions/common/components/common/components/common/model/betting.model';

@Injectable({
  providedIn: 'root',
})
export class UtilsService {
  static dateToUnix(date: Date): number {
    return date.getTime() / 1000;
  }

  static unixToDate(unixTs: number): Date {
    return new Date(unixTs * 1000);
  }

  static listToMap<T, K extends keyof T>(list: T[], key: K): Map<T[K], T> {
    return list.reduce((map: Map<T[K], T>, item: T) => {
      map.set(item[key], item);

      return map;
    }, new Map<T[K], T>());
  }

  static numToLetter(num: number): string {
    return String.fromCharCode(65 + num);
  }

  static genRangeOptions(num: number): IBettingOption[] {
    return [...Array(num + 1)].map((j, i) => i)
      .map((i) => ({
        value: i,
        name: i.toString(),
      }));
  }

  static unixTimestampToDate(data: UnixDateProps): TimestampsProps {
    return {
      createdAt: UtilsService.unixToDate(data.createdAt),
      updatedAt: UtilsService.unixToDate(data.updatedAt),
    };
  }

  static updateState<T>(state: T, key: keyof T, value: any): T {
    return {
      ...(state as any),
      [key]: value,
    };
  }

  static isOnlyError(ctrl: AbstractControl, error: string): boolean {
    return UtilsService.hasError(ctrl, error) && Object.entries(ctrl.errors!).length === 1;
  }

  static hasError(ctrl: AbstractControl, error: string): boolean {
    if (!ctrl.errors) {
      return false;
    }

    return !!ctrl.errors[error];
  }

  static dtoToModel<D extends UnixDateProps>(dto: D): Model<D> {
    return {
      ...dto,
      ...UtilsService.unixTimestampToDate(dto),
    };
  }

  static genRandomString(length: number): string {
    const seed = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

    return (
      [...Array(length)]
        // eslint-disable-next-line no-bitwise
        .map(() => seed[~~(Math.random() * seed.length)])
        .join('')
    );
  }

  static camelToText(str: string, capitalize = false): string {
    const result = str.replace(/([A-Z])/g, ' $1');

    if (capitalize) {
      return result.charAt(0).toUpperCase() + result.slice(1);
    }

    return result;
  }

  static dateToDateTimeLocale(date: Date): string {
    const d = new Date(date.getTime());
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());

    return d.toISOString().slice(0, 16);
  }

  static getBase64(file: File): Promise<string> {
    return new Promise((res, rej) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        res(reader.result as string);
      };
      reader.onerror = error => {
        rej(error);
      };
    });
  }

  static removeUndefined<T extends Record<any, any>>(obj: T): Partial<T> {
    return JSON.parse(JSON.stringify(obj));
  }

  static provideDiff<T extends Record<string, any>, K extends Record<string, any>>(a: K, b: T): Partial<T> {
    return Object.keys(b).reduce((acc, key: keyof T) => {
      if (a[key as string] !== b[key]) {
        acc[key] = b[key];
      }

      return acc;
    }, {} as Partial<T>);
  }

  static genArray(length: number): number[] {
    return Array.from(Array(length).keys());
  }
}

interface UnixDateProps {
  createdAt: number;
  updatedAt: number;
}
